import { createElement } from "react";
import {
  createRoute,
  lazyRouteComponent,
  type AnyRoute,
} from "@tanstack/react-router";

import type { FeatureRouteContext } from "@nasam/kernel";
import { POI_CREATE, POI_UPDATE, POI_VIEW } from "../../../../permissions";
import { requirePermission } from "../../../../presentation/routes/requirePermission";
import type { PoisRepository } from "../../domain";
import { poiCreateSearchSchema, type PoiCreateSearch } from "./searchSchemas";

const PoisListScreen = lazyRouteComponent(
  () => import("../PoisListScreen"),
  "PoisListScreen",
);
const PoiCreateScreen = lazyRouteComponent(
  () => import("../PoiCreateScreen"),
  "PoiCreateScreen",
);
const PoiDetailScreen = lazyRouteComponent(
  () => import("../PoiDetailScreen"),
  "PoiDetailScreen",
);
const PoiEditScreen = lazyRouteComponent(
  () => import("../PoiEditScreen"),
  "PoiEditScreen",
);

export interface PoisRouteDeps {
  readonly context: FeatureRouteContext;
  /** e.g. `/fleet/pois`. */
  readonly basePath: string;
  readonly pois: PoisRepository;
}

export function createPoisRoutes({
  context,
  basePath,
  pois,
}: PoisRouteDeps): AnyRoute[] {
  const createPoiRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${basePath}/create`,
    beforeLoad: requirePermission([POI_CREATE]),
    validateSearch: poiCreateSearchSchema,
    component: function PoiCreateRouteScreen() {
      // Typed by hand: the route refers to itself here (same convention as
      // supervisorAssignments' useParams).
      const { lat, lng } =
        createPoiRoute.useSearch() as unknown as PoiCreateSearch;
      return createElement(PoiCreateScreen, {
        repository: pois,
        basePath,
        // Both, or neither: a half location isn't one.
        initialLocation:
          lat === undefined || lng === undefined
            ? null
            : { latitude: lat, longitude: lng },
      });
    },
  });

  // '$poiId' after the literal 'create' above, so it's never taken for an id.
  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${basePath}/$poiId`,
    beforeLoad: requirePermission([POI_VIEW]),
    component: function PoiDetailRouteScreen() {
      const { poiId } = detailRoute.useParams() as unknown as {
        poiId: string;
      };
      return createElement(PoiDetailScreen, {
        repository: pois,
        basePath,
        poiId,
      });
    },
  });

  // '$poiId/edit' before the bare '$poiId' (same convention as
  // supervisorAssignments' edit and detail routes).
  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${basePath}/$poiId/edit`,
    beforeLoad: requirePermission([POI_UPDATE]),
    component: function PoiEditRouteScreen() {
      const { poiId } = editRoute.useParams() as unknown as {
        poiId: string;
      };
      return createElement(PoiEditScreen, {
        repository: pois,
        basePath,
        poiId,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: basePath,
      beforeLoad: requirePermission([POI_VIEW]),
      component: () =>
        createElement(PoisListScreen, { repository: pois, basePath }),
    }),
    createPoiRoute,
    editRoute,
    detailRoute,
  ];
}
